import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { DevicesClass, DeviceTypes } from '@app/core/classes';
import { AppStaticURL } from '@app/app.settings';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import {
  AvailabilityOption,
  BookingPerformerHiringStatus,
  EquipmentAvailability,
  PerformerHiringVm,
  resolveHiringConditions
} from '@app/modules/booking-modules/booking.model';
import { AccountService } from '@app/modules/account-modules/account.service';
import { getActiveBookingCustomerId } from '@app/modules/account-modules/account.model';
import { PerformersService } from '@app/modules/account-modules/performers.service';
import { PerformerProfileDto, SocialLinkDto, socialPlatformFromApiValue } from '@app/modules/account-modules/performers.model';
import { PLATFORM_ICON_MAP, PlatformIconConfig, SOCIAL_PLATFORMS } from '@app/modules/performers-modules/client/performer-form.constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { ConfirmationService } from 'primeng/api';

@UntilDestroy()
@Component({
  selector: 'app-account-hirings',
  templateUrl: './hirings.component.html',
  styleUrls: ['./hirings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HiringsComponent extends DevicesClass implements OnInit {
  readonly DTypes = DeviceTypes;
  readonly StaticURL = AppStaticURL;
  readonly pageSize = 20;
  readonly statusOptions = [
    { name: 'Все статусы', value: '' },
    { name: 'Ожидает', value: BookingPerformerHiringStatus.Pending },
    { name: 'Принята', value: BookingPerformerHiringStatus.Accepted },
    { name: 'Отклонена', value: BookingPerformerHiringStatus.Declined },
    { name: 'Завершена', value: BookingPerformerHiringStatus.Completed },
    { name: 'Отозвана', value: BookingPerformerHiringStatus.Withdrawn },
    { name: 'Неуспешна', value: BookingPerformerHiringStatus.Unsuccessful }
  ];

  bookingCustomerId: string | null = null;
  loading = false;
  loadError: string | null = null;
  items: PerformerHiringVm[] = [];
  page = 1;
  hasMore = false;
  from = '';
  to = '';
  status: BookingPerformerHiringStatus | '' = '';
  actionLoadingId: string | null = null;

  contactsDialogVisible = false;
  contactsLoading = false;
  contactsLoadError: string | null = null;
  contactsProfile: PerformerProfileDto | null = null;
  contactsPerformerName: string | null = null;

  constructor(
    injector: Injector,
    public cdr: ChangeDetectorRef,
    private bookingService: BookingService,
    private accountService: AccountService,
    private performersService: PerformersService,
    private confirmationService: ConfirmationService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.resolveBookingCustomerAndLoad();
  }

  onApplyFilters(): void {
    this.page = 1;
    this.loadHirings();
  }

  onResetFilters(): void {
    this.from = '';
    this.to = '';
    this.status = '';
    this.page = 1;
    this.loadHirings();
  }

  onPrevPage(): void {
    if (this.page <= 1 || this.loading) {
      return;
    }
    this.page -= 1;
    this.loadHirings();
  }

  onNextPage(): void {
    if (!this.hasMore || this.loading) {
      return;
    }
    this.page += 1;
    this.loadHirings();
  }

  statusLabel(status: BookingPerformerHiringStatus | null | undefined): string {
    const map: Record<string, string> = {
      [BookingPerformerHiringStatus.Pending]: 'Ожидает',
      [BookingPerformerHiringStatus.Accepted]: 'Принята',
      [BookingPerformerHiringStatus.Declined]: 'Отклонена',
      [BookingPerformerHiringStatus.Completed]: 'Исполнена',
      [BookingPerformerHiringStatus.Withdrawn]: 'Отозвана',
      [BookingPerformerHiringStatus.Unsuccessful]: 'Не состоялась'
    };
    return status && map[status] ? map[status] : '—';
  }

  get showPager(): boolean {
    return this.page > 1 || this.hasMore;
  }

  performerPhoto(item: PerformerHiringVm): string | null {
    const p = item?.performer;
    if (!p) {
      return null;
    }
    const rel = p.image?.thumbnailPath || p.image?.path || p.avatarPath || p.picPath || null;
    if (!rel) {
      return null;
    }
    return /^https?:\/\//i.test(rel) ? rel : (this.StaticURL + rel.replace(/^\//, ''));
  }

  formatHiringDate(item: PerformerHiringVm): string {
    const d = item?.createdAt;
    if (!d) {
      return '—';
    }
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) {
      return '—';
    }
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = dt.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  }

  conditionsSummary(item: PerformerHiringVm): string {
    const c = resolveHiringConditions(item);
    if (!c) {
      return '—';
    }
    const parts: string[] = [];
    const amount = c?.honorarium?.amount;
    const isNegotiable = !!c?.honorarium?.isNegotiable;
    if (isNegotiable) {
      parts.push('Оплата: договорная');
    } else if (typeof amount === 'number' && amount > 0) {
      parts.push(`Оплата: ${new Intl.NumberFormat('ru-RU').format(amount)} ₽`);
    }

    const eq = c?.equipment?.availability;
    if (eq === EquipmentAvailability.Yes) {
      parts.push('Аппаратура: есть');
    } else if (eq === EquipmentAvailability.No) {
      parts.push('Аппаратура: нет');
    } else if (eq === EquipmentAvailability.Text) {
      parts.push('Аппаратура: по договоренности');
    }

    const acc = c?.coversAccommodation;
    if (acc === AvailabilityOption.Yes) {
      parts.push('Проживание: нужно');
    } else if (acc === AvailabilityOption.No) {
      parts.push('Проживание: не нужно');
    } else if (acc === AvailabilityOption.OnRequest) {
      parts.push('Проживание: по запросу');
    }

    return parts.length ? parts.join(', ') : '—';
  }

  statusClass(status: BookingPerformerHiringStatus | null | undefined): string {
    if (status === BookingPerformerHiringStatus.Pending) return 'pending';
    if (status === BookingPerformerHiringStatus.Accepted) return 'accepted';
    if (status === BookingPerformerHiringStatus.Declined) return 'declined';
    if (status === BookingPerformerHiringStatus.Completed) return 'completed';
    if (status === BookingPerformerHiringStatus.Withdrawn) return 'withdrawn';
    if (status === BookingPerformerHiringStatus.Unsuccessful) return 'unsuccessful';
    return 'unknown';
  }

  canWithdraw(item: PerformerHiringVm): boolean {
    return item?.status === BookingPerformerHiringStatus.Pending;
  }

  canMarkResult(item: PerformerHiringVm): boolean {
    return item?.status === BookingPerformerHiringStatus.Accepted;
  }

  actionDisabled(item: PerformerHiringVm): boolean {
    return this.loading || !item?.id || this.actionLoadingId === item.id;
  }

  confirmWithdraw(item: PerformerHiringVm): void {
    if (!this.canWithdraw(item) || this.actionLoadingId) {
      return;
    }
    this.confirmationService.confirm({
      key: 'hirings',
      header: 'Отменить приглашение?',
      message: 'Заявка будет отменена, исполнитель получит уведомление.',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.runWithdraw(item)
    });
  }

  confirmMarkPerformed(item: PerformerHiringVm): void {
    if (!this.canMarkResult(item) || this.actionLoadingId) {
      return;
    }
    this.confirmationService.confirm({
      key: 'hirings',
      header: 'Отметить как исполненную?',
      message: 'Статус заявки изменится на «Исполнена».',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.runMarkPerformed(item)
    });
  }

  confirmMarkUnsuccessful(item: PerformerHiringVm): void {
    if (!this.canMarkResult(item) || this.actionLoadingId) {
      return;
    }
    this.confirmationService.confirm({
      key: 'hirings',
      header: 'Отметить, что не состоялось?',
      message: 'Статус заявки изменится на «Не состоялась».',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.runMarkUnsuccessful(item)
    });
  }

  showPerformerContacts(item: PerformerHiringVm): void {
    if (!this.canMarkResult(item)) {
      return;
    }
    const performerId = item?.performer?.id;
    if (!performerId) {
      return;
    }
    this.contactsPerformerName = item.performer?.name ?? null;
    this.contactsProfile = null;
    this.contactsLoadError = null;
    this.contactsLoading = true;
    this.contactsDialogVisible = true;
    this.cdr.markForCheck();
    this.performersService
      .getPerformerProfileById(performerId)
      .pipe(
        finalize(() => {
          this.contactsLoading = false;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe(
        p => {
          this.contactsProfile = p;
        },
        err => {
          this.contactsLoadError =
            (err && (err.message || err.error?.message)) || 'Не удалось загрузить контакты.';
        }
      );
  }

  onContactsDialogHide(): void {
    this.contactsProfile = null;
    this.contactsLoadError = null;
    this.contactsPerformerName = null;
  }

  contactsDialogHeader(): string {
    const n = (this.contactsPerformerName || '').trim();
    return n ? `Контакты: ${n}` : 'Контакты исполнителя';
  }

  phoneImageUrl(profile: PerformerProfileDto | null): string | null {
    const img = profile?.phoneImage;
    if (!img) {
      return null;
    }
    const rel = img.thumbnailPath || img.path;
    return rel ? this.StaticURL + rel.replace(/^\//, '') : null;
  }

  contactsHasAny(profile: PerformerProfileDto | null): boolean {
    if (!profile) {
      return false;
    }
    if (this.phoneImageUrl(profile)) {
      return true;
    }
    const bp = profile.bookingPhone;
    if (bp?.hasTelegram || bp?.hasMax) {
      return true;
    }
    return !!(profile.socialLinks && profile.socialLinks.length);
  }

  getPlatformIcon(platformValue: number): PlatformIconConfig {
    return PLATFORM_ICON_MAP[platformValue] ?? { type: 'svg', src: 'assets/images/icons/website.svg' };
  }

  platformForLink(link: SocialLinkDto): number | null {
    return socialPlatformFromApiValue(link.platform);
  }

  platformLabel(link: SocialLinkDto): string {
    const raw = link.platform;
    const en = socialPlatformFromApiValue(raw);
    if (en === null) {
      const r = raw as unknown;
      if (r !== undefined && r !== null && r !== '') {
        return String(r);
      }
      return 'Ссылка';
    }
    const row = SOCIAL_PLATFORMS.find(p => p.value === en);
    return row?.name ?? String(raw);
  }

  private runWithdraw(item: PerformerHiringVm): void {
    const requestId = item?.id;
    if (!this.bookingCustomerId || !requestId || !this.canWithdraw(item) || this.actionLoadingId) {
      return;
    }
    this.actionLoadingId = requestId;
    this.bookingService.withdrawHiringRequest(this.bookingCustomerId, requestId)
      .pipe(
        finalize(() => {
          this.actionLoadingId = null;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe(
        () => this.loadHirings(),
        err => {
          this.loadError = (err && (err.message || err.error?.message)) || 'Не удалось отменить приглашение.';
          this.cdr.markForCheck();
        }
      );
  }

  private runMarkPerformed(item: PerformerHiringVm): void {
    const requestId = item?.id;
    if (!this.bookingCustomerId || !requestId || !this.canMarkResult(item) || this.actionLoadingId) {
      return;
    }
    this.actionLoadingId = requestId;
    const performedAt = item.performanceDate || item.performedAt || new Date().toISOString();
    this.bookingService.markHiringPerformed(this.bookingCustomerId, requestId, performedAt)
      .pipe(
        finalize(() => {
          this.actionLoadingId = null;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe(
        () => this.loadHirings(),
        err => {
          this.loadError = (err && (err.message || err.error?.message)) || 'Не удалось отметить заявку как исполненную.';
          this.cdr.markForCheck();
        }
      );
  }

  private runMarkUnsuccessful(item: PerformerHiringVm): void {
    const requestId = item?.id;
    if (!this.bookingCustomerId || !requestId || !this.canMarkResult(item) || this.actionLoadingId) {
      return;
    }
    this.actionLoadingId = requestId;
    this.bookingService.markHiringUnsuccessful(this.bookingCustomerId, requestId)
      .pipe(
        finalize(() => {
          this.actionLoadingId = null;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe(
        () => this.loadHirings(),
        err => {
          this.loadError = (err && (err.message || err.error?.message)) || 'Не удалось отметить, что выступление не состоялось.';
          this.cdr.markForCheck();
        }
      );
  }

  private resolveBookingCustomerAndLoad(): void {
    this.loading = true;
    this.loadError = null;
    this.cdr.markForCheck();
    this.accountService
      .getMyInfo()
      .pipe(untilDestroyed(this))
      .subscribe(
        user => {
          this.accountService.onSetUserRole(user);
          this.bookingCustomerId = getActiveBookingCustomerId(user);
          if (!this.bookingCustomerId) {
            this.loading = false;
            this.loadError = 'Профиль заказчика не найден.';
            this.cdr.markForCheck();
            return;
          }
          this.loadHirings();
        },
        () => {
          this.loading = false;
          this.loadError = 'Не удалось загрузить данные аккаунта.';
          this.cdr.markForCheck();
        }
      );
  }

  private loadHirings(): void {
    if (!this.bookingCustomerId) {
      return;
    }
    this.loading = true;
    this.loadError = null;
    this.cdr.markForCheck();
    this.bookingService
      .getCustomerHirings(this.bookingCustomerId, {
        page: this.page,
        size: this.pageSize,
        from: this.from || null,
        to: this.to || null,
        status: this.status || null
      })
      .pipe(untilDestroyed(this))
      .subscribe(
        list => {
          this.items = list || [];
          this.hasMore = (this.items?.length || 0) >= this.pageSize;
          this.loading = false;
          this.cdr.markForCheck();
        },
        err => {
          this.loading = false;
          this.items = [];
          this.hasMore = false;
          this.loadError =
            (err && (err.message || err.error?.message)) || 'Не удалось загрузить список заявок.';
          this.cdr.markForCheck();
        }
      );
  }
}
